// Outer
import * as Sentry from '@sentry/nextjs';
import React from 'react';
import * as yup from 'yup';
import {FormikProvider, useFormik} from 'formik';
import {Box, Button, FormGroup, Typography, MenuItem} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useMutation} from 'react-query';

// Inner
import FormikTextField from '@/common/components/forms/TextField';
import ReactQuillField from '@/common/components/forms/ReactQuillField';
import {useApiContext} from '@/common/contexts/ApiContext';
import {ApiError} from '@/common/api/types';
import {FormikCustomSelect} from '@/common/components/forms/Select';

// Methods
function getInitialValues() {
  return {
    categoryId: -1,
    question: '',
    answer: '',
  };
}

const validationSchema = yup.object({
  categoryId: yup.number().min(0),
  question: yup.string().required(),
  answer: yup.string(),
});

function useCreateKnowledgeBaseForm(onClose: () => void) {
  const {enqueueSnackbar} = useSnackbar();
  const {apiClient} = useApiContext();

  const createKnowledgeBaseMutation = useMutation<any, ApiError | Error, any>(
    apiClient.knowledgebase.create,
    {
      onSuccess: onSiteKnowledgebaseSuccess,
      onError,
    },
  );

  const initialValues = getInitialValues();

  const formik = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  function onSiteKnowledgebaseSuccess() {
    enqueueSnackbar('Knowledge Base created', {variant: 'success'});

    formik.setSubmitting(false);
    onClose();
  }
  function onError(error: ApiError | Error) {
    enqueueSnackbar(error.message, {variant: 'error'});
    Sentry.captureException(error);
    formik.setSubmitting(false);
  }

  function onSubmit(values: any) {
    createKnowledgeBaseMutation.mutate(values);
  }

  return {
    formik,
  };
}

export default function CreateKnowledgeBaseForm({
  onClose,
  knowledgeBaseCategories,
}: any) {
  const {formik} = useCreateKnowledgeBaseForm(onClose);

  return (
    <FormikProvider value={formik}>
      <Box margin={2} display="flex" flexDirection="column">
        <FormGroup>
          <Box marginTop={2}>
            <Typography>Create Knowledge Base</Typography>
          </Box>

          <Box marginTop={2}>
            <FormikTextField
              fullWidth
              id="question"
              name="question"
              type="text"
              label="Question"
              variant="outlined"
              tooltiptitle="Enter the question of the entity"
            />
          </Box>

          <Box marginTop={2} sx={{width: '100%'}}>
            <FormikCustomSelect
              label="Category"
              name="categoryId"
              tooltiptitle="Select the Category"
            >
              {knowledgeBaseCategories.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </FormikCustomSelect>
          </Box>

          <Box marginTop={2}>
            <ReactQuillField
              id="answer"
              name="answer"
              label="Answer"
              tooltiptitle="Enter an answer to help you later recall the details of this Knowledge Base"
            />
          </Box>
        </FormGroup>

        <Box marginTop={1} marginBottom={1}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={false} // TODO: Add Logic
            onClick={formik.submitForm}
          >
            Save
          </Button>
        </Box>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </FormikProvider>
  );
}
